/**
 * Beat 7's two dogs: frightening because the rain preserves where Rosalie hid.
 *
 * The pose maths below is deliberately a second copy of `corridorDirector.ts`'s
 * dog driver. Extracting it would edit beat 4's shipped director in the same
 * session that ships beat 7, which is a worse trade than one explicit duplicate.
 * The extraction is filed in `docs/backlog.md` against the next beat with dogs;
 * if the two copies ever disagree about how a dog's neck works,
 * `corridorDirector.ts` is the one that has been looked at on screen.
 *
 * `step` owns every observable choice. `update` only presents those choices:
 * it must never write a value that `step` reads, or a replay will become a
 * property of display timing rather than of the fixed simulation clock (D-045).
 */

import * as THREE from 'three';
import { applyDogPose } from '@/anim/dogPose';
import { mulberry32 } from '@/audio/prng';
import { dogAsset, findDogParts, type DogParts } from '@/models/dog';
import { disposeAsset } from '@/models/materials';
import {
  SCENT_TUNING,
  SHELTER_MIN_CONCEALMENT,
  ScentTrail,
  Tracker,
  type TrackerMode,
} from '@/systems/scent';
import { bestConcealment, type CoverVolume } from '@/systems/stealth';

export type DogPose = 'heel' | 'working' | 'casting' | 'driving';

export interface OutDogsOptions {
  /** The room's cover volumes. Cover from eyes is shelter from rain in beat 7. */
  readonly cover: readonly CoverVolume[];
  /** Where the handler stands. The dogs start at his feet. */
  readonly handlerAt: { readonly x: number; readonly z: number };
  /** Ground height at a point — the field is not flat. Supplied by the room. */
  readonly groundY: (x: number, z: number) => number;
  /** Dogs never go below this z. The near bank of the ditch. */
  readonly bankZ: number;
  /** Nose down on a place. Fired per dog, sparsely — not every step. */
  readonly onSnuff: (at: { x: number; y: number; z: number }) => void;
  /** Claws on wet concrete. Fired sparsely while a dog is moving on the apron. */
  readonly onClaws: (at: { x: number; y: number; z: number }) => void;
}

export interface OutDogsDebug {
  depositing: boolean;
  driving: boolean;
  standingDown: boolean;
  /** Per dog: position, pose, tracker mode, and its current target. */
  dogs: readonly {
    x: number;
    z: number;
    pose: DogPose;
    mode: TrackerMode;
    targetX: number;
    targetZ: number;
  }[];
  /** Trail size, and — the mechanic's own number — how many samples are sheltered. */
  samples: number;
  shelteredSamples: number;
}

const DOG_COUNT = 2;
const WORKING_SPEED = 1.1;
const DRIVING_SPEED = 2.6;
const TRACKER_TURN_RATE = 6;
const TRACKER_GIVE_UP_SECONDS = 90;
const LATERAL_OFFSET = 0.45;
const EPSILON = 0.000000001;

const CLAWS = { minimumMetres: 0.34, rangeMetres: 0.22, seed: 0x0d065 } as const;

type DogRuntime = {
  readonly mesh: THREE.Object3D;
  readonly parts: DogParts;
  readonly tracker: Tracker;
  readonly lateralOffset: number;
  readonly clawsRandom: () => number;
  pose: DogPose;
  targetX: number;
  targetZ: number;
  previousMode: TrackerMode;
  clawMetres: number;
  nextClawMetres: number;
};

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`out dogs: ${label} must be finite`);
}

function clampedFrameDt(frameDt: number): number {
  return Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;
}

/** A concrete-only tick is varied deterministically, never with frame timing. */
function nextClawMetres(random: () => number): number {
  return CLAWS.minimumMetres + random() * CLAWS.rangeMetres;
}

export class OutDogs {
  readonly #options: OutDogsOptions;
  readonly #trail: ScentTrail;
  readonly #dogs: readonly DogRuntime[];
  #depositing = false;
  #driving = false;
  #standingDown = false;
  #working = false;
  #closestRange = Infinity;
  // Presentation only. This clock is intentionally absent from debug state and
  // is never read in `step`; changing a frame rate cannot move a dog or scent.
  #gaitClock = 0;

  constructor(scene: THREE.Object3D, options: OutDogsOptions) {
    finite(options.handlerAt.x, 'handlerAt.x');
    finite(options.handlerAt.z, 'handlerAt.z');
    finite(options.bankZ, 'bankZ');
    this.#options = options;
    this.#trail = new ScentTrail({
      sheltered: (x, z) => bestConcealment(
        { x, y: this.#groundY(x, z) + 0.16, z },
        options.cover,
      ) >= SHELTER_MIN_CONCEALMENT,
    });

    this.#seedMensTrail();
    const dogs: DogRuntime[] = [];
    for (let index = 0; index < DOG_COUNT; index += 1) {
      const lateralOffset = index === 0 ? -LATERAL_OFFSET : LATERAL_OFFSET;
      const x = options.handlerAt.x + lateralOffset;
      const z = Math.max(options.bankZ, options.handlerAt.z);
      const mesh = dogAsset.create();
      mesh.position.set(x, this.#groundY(x, z), z);
      const random = mulberry32(CLAWS.seed + index);
      const tracker = new Tracker({
        speed: WORKING_SPEED,
        turnRate: TRACKER_TURN_RATE,
        giveUpSeconds: TRACKER_GIVE_UP_SECONDS,
        start: { x, y: this.#groundY(x, z), z },
      });
      dogs.push({
        mesh,
        parts: findDogParts(mesh),
        tracker,
        lateralOffset,
        clawsRandom: random,
        pose: 'heel',
        targetX: x,
        targetZ: z,
        previousMode: tracker.state.mode,
        clawMetres: 0,
        nextClawMetres: nextClawMetres(random),
      });
      scene.add(mesh);
    }
    this.#dogs = dogs;
  }

  /** The trail she is laying. The director gates deposits; see `depositing`. */
  get trail(): ScentTrail {
    return this.#trail;
  }

  /** Closest dog's distance to her, metres, or Infinity. The capture test reads this. */
  get closestRange(): number {
    return this.#closestRange;
  }

  /** What the gates read instead of inferring state from pixels. */
  get debug(): OutDogsDebug {
    return {
      depositing: this.#depositing,
      driving: this.#driving,
      standingDown: this.#standingDown,
      dogs: this.#dogs.map((dog) => ({
        x: dog.mesh.position.x,
        z: dog.mesh.position.z,
        pose: dog.pose,
        mode: dog.tracker.state.mode,
        targetX: dog.targetX,
        targetZ: dog.targetZ,
      })),
      samples: this.#trail.count,
      shelteredSamples: this.#trail.samples.filter((sample) => sample.lifetime === SCENT_TUNING.lifetimeSeconds).length,
    };
  }

  /** Fixed step. Deterministic. Writes dog positions, poses and the trail. */
  step(dt: number, self: { x: number; y: number; z: number } | null): void {
    if (!Number.isFinite(dt) || dt < 0) throw new Error('out dogs: step dt must be finite and not negative');
    if (self !== null) {
      finite(self.x, 'self.x');
      finite(self.y, 'self.y');
      finite(self.z, 'self.z');
    }

    // The stage gate is load-bearing, not an optimisation. Under the trailer
    // Rosalie is already inside the dogs' nose radius and sheltered; depositing
    // during stage 1 would preserve 90 seconds of evidence and make them walk
    // straight to her while the player is meant to watch the men’s trail. Beat
    // 4's `TRAIL_STAGES` exists for the same staging reason.
    this.#trail.step(dt, this.#depositing && !this.#standingDown ? self : null);

    if (this.#standingDown) {
      for (const dog of this.#dogs) this.#holdAtBank(dog);
    } else if (this.#driving) {
      if (self !== null) {
        for (const dog of this.#dogs) this.#stepDrivingDog(dog, dt, self);
      }
    } else if (this.#working) {
      for (const dog of this.#dogs) this.#stepWorkingDog(dog, dt);
    }

    this.#updateClosestRange(self);
  }

  /** Render step. Legs, necks, tails. Reads fixed-step state; writes no state. */
  update(frameDt: number): void {
    this.#gaitClock += clampedFrameDt(frameDt);
    for (const dog of this.#dogs) this.#animateDog(dog);
  }

  /** Stage 1: they work the men's ground and do not come to her. */
  workTheWall(): void {
    if (this.#standingDown || this.#driving) return;
    this.#working = true;
    for (const dog of this.#dogs) {
      dog.tracker.release(this.#dogPosition(dog));
      dog.previousMode = dog.tracker.state.mode;
      dog.pose = 'working';
    }
  }

  /** Stage 2 onward: her trail starts being laid. */
  startDepositing(): void {
    if (!this.#standingDown) this.#depositing = true;
  }

  /** The stage-2 backstop: they stop being patient and come straight at her. */
  drive(): void {
    if (this.#standingDown) return;
    this.#driving = true;
    this.#working = false;
    for (const dog of this.#dogs) {
      dog.tracker.release(this.#dogPosition(dog));
      dog.previousMode = dog.tracker.state.mode;
      dog.pose = 'driving';
    }
  }

  /** Stage 4: they stop at the bank. Also stops the trail. */
  standDown(): void {
    this.#standingDown = true;
    this.#driving = false;
    this.#working = false;
    this.#depositing = false;
    for (const dog of this.#dogs) {
      this.#holdAtBank(dog);
      dog.tracker.release(this.#dogPosition(dog));
      dog.previousMode = dog.tracker.state.mode;
      dog.pose = 'casting';
    }
  }

  /**
   * Return both dogs to the handler while a capture is blacked out.
   *
   * The director restarts its stage-2 patience clock after this call. Leaving
   * these private driver fields live made that restart fictitious: the dogs still
   * targeted Rosalie's new position and immediately surrounded her under the
   * trailer. Their scent trail is deliberately retained; the next pressure phase
   * is still the same second-capture mercy design, just not an instant recapture.
   */
  resetAfterCapture(): void {
    if (this.#standingDown) return;
    this.#driving = false;
    this.#working = false;
    for (const dog of this.#dogs) {
      const x = this.#options.handlerAt.x + dog.lateralOffset;
      const z = Math.max(this.#options.bankZ, this.#options.handlerAt.z);
      dog.mesh.position.set(x, this.#groundY(x, z), z);
      dog.targetX = x;
      dog.targetZ = z;
      dog.pose = 'heel';
      dog.tracker.hold();
      dog.previousMode = dog.tracker.state.mode;
      dog.clawMetres = 0;
      dog.nextClawMetres = nextClawMetres(dog.clawsRandom);
    }
    this.#closestRange = Infinity;
  }

  dispose(): void {
    for (const dog of this.#dogs) {
      dog.mesh.removeFromParent();
      disposeAsset(dog.mesh);
    }
  }

  /**
   * Seed the men’s sheltered route through the normal deposit path.
   *
   * This deliberately does not build samples by hand. If a room fails to cover
   * this lee-of-shed line, rain expires it in seconds and the dogs have nothing
   * to work; that is a fault in the room the beat must expose, not paper over.
   */
  #seedMensTrail(): void {
    const fromX = this.#options.handlerAt.x - 10;
    const toX = this.#options.handlerAt.x + 2;
    const z = this.#options.handlerAt.z - 0.6;
    const count = Math.round((toX - fromX) / SCENT_TUNING.depositEvery);
    for (let index = 0; index <= count; index += 1) {
      const x = fromX + index * SCENT_TUNING.depositEvery;
      this.#trail.step(0, { x, y: this.#groundY(x, z), z });
    }
  }

  #stepWorkingDog(dog: DogRuntime, dt: number): void {
    const state = dog.tracker.step(dt, this.#trail);
    const modeChangedToCasting = dog.previousMode === 'tracking' && state.mode === 'casting';
    dog.previousMode = state.mode;

    if (state.mode === 'tracking') {
      dog.pose = 'working';
      // `Tracker` owns the truthful route. The scene copy is shifted sideways by
      // one authored half-metre so two animals read as a pair instead of one dog
      // drawn twice; the sign is per dog and never changes with frames or RNG.
      const targetX = state.x + Math.cos(state.facing) * dog.lateralOffset;
      const targetZ = Math.max(this.#options.bankZ, state.z - Math.sin(state.facing) * dog.lateralOffset);
      dog.targetX = targetX;
      dog.targetZ = targetZ;
      this.#placeDog(dog, targetX, targetZ, dt, WORKING_SPEED);
    } else {
      dog.pose = 'casting';
      dog.targetX = dog.mesh.position.x;
      dog.targetZ = Math.max(this.#options.bankZ, dog.mesh.position.z);
      this.#placeDog(dog, dog.targetX, dog.targetZ, dt, WORKING_SPEED);
      if (modeChangedToCasting) this.#options.onSnuff(this.#dogPosition(dog));
    }
  }

  #stepDrivingDog(dog: DogRuntime, dt: number, self: { x: number; y: number; z: number }): void {
    dog.pose = 'driving';
    // Driving is straight pursuit, but it is still two animals: preserve the
    // same stable half-metre shoulder separation rather than letting both mesh
    // roots occupy Rosalie's exact point at the ditch bank. The run is authored
    // along Z, so X is the stable lateral axis even once the bank clamps Z.
    dog.targetX = self.x + dog.lateralOffset;
    dog.targetZ = Math.max(this.#options.bankZ, self.z);
    this.#placeDog(dog, dog.targetX, dog.targetZ, dt, DRIVING_SPEED);
    // A driver is deliberately not reading scent. Keep the tracker where the
    // dog is, in its explicit casting state, so debug cannot imply it is still
    // consuming an unrelated line behind Rosalie.
    dog.tracker.release(this.#dogPosition(dog));
    dog.previousMode = dog.tracker.state.mode;
  }

  #holdAtBank(dog: DogRuntime): void {
    const x = dog.mesh.position.x;
    const z = Math.max(this.#options.bankZ, dog.mesh.position.z);
    dog.targetX = x;
    dog.targetZ = z;
    this.#placeDog(dog, x, z, 0, 0);
    dog.pose = 'casting';
  }

  #placeDog(dog: DogRuntime, targetX: number, targetZ: number, dt: number, speed: number): void {
    const startX = dog.mesh.position.x;
    const startZ = Math.max(this.#options.bankZ, dog.mesh.position.z);
    const safeTargetZ = Math.max(this.#options.bankZ, targetZ);
    const dx = targetX - startX;
    const dz = safeTargetZ - startZ;
    const distance = Math.hypot(dx, dz);
    const travelled = distance <= EPSILON ? 0 : Math.min(distance, speed * dt);
    const x = distance <= EPSILON ? startX : startX + (dx / distance) * travelled;
    const z = distance <= EPSILON ? startZ : Math.max(this.#options.bankZ, startZ + (dz / distance) * travelled);
    const moved = Math.hypot(x - startX, z - startZ);
    dog.mesh.position.set(x, this.#groundY(x, z), z);
    if (moved > EPSILON) dog.mesh.rotation.y = Math.atan2(x - startX, z - startZ);
    this.#stepClaws(dog, moved);
  }

  #stepClaws(dog: DogRuntime, moved: number): void {
    if (moved <= EPSILON || dog.pose === 'casting' || dog.mesh.position.z <= 0) return;
    dog.clawMetres += moved;
    while (dog.clawMetres + EPSILON >= dog.nextClawMetres) {
      dog.clawMetres -= dog.nextClawMetres;
      this.#options.onClaws(this.#dogPosition(dog));
      dog.nextClawMetres = nextClawMetres(dog.clawsRandom);
    }
  }

  #animateDog(dog: DogRuntime): void {
    // This beat's pose vocabulary, mapped onto what the animal is doing. The
    // arithmetic is `src/anim/dogPose.ts` now — it was a verbatim copy of beat
    // 4's, and beat 10 would have been the third. **The tail sways out here and
    // does not in beat 4**, which is the one place the two copies disagreed and
    // is preserved as an input rather than reconciled: this dog is quartering a
    // wet field off the lead, beat 4's is at a handler's heel.
    const moving = dog.pose === 'working' || dog.pose === 'driving';
    applyDogPose(dog.parts, {
      walking: moving,
      noseDown: dog.pose === 'working',
      casting: dog.pose === 'casting',
      tail: true,
      clock: this.#gaitClock,
    });
  }

  #dogPosition(dog: DogRuntime): { x: number; y: number; z: number } {
    const { x, y, z } = dog.mesh.position;
    return { x, y, z };
  }

  #groundY(x: number, z: number): number {
    const y = this.#options.groundY(x, z);
    finite(y, 'groundY result');
    return y;
  }

  #updateClosestRange(self: { x: number; y: number; z: number } | null): void {
    if (self === null) {
      this.#closestRange = Infinity;
      return;
    }
    this.#closestRange = this.#dogs.reduce((closest, dog) => {
      const position = dog.mesh.position;
      return Math.min(closest, Math.hypot(position.x - self.x, position.y - self.y, position.z - self.z));
    }, Infinity);
  }
}
